import type { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ControlledNumberFormInput } from '#components/form/FormInput.tsx';
import type { OrderSummary } from '#domain/Order.ts';

import { CapacityWarning } from './CapacityWarning.tsx';
import type { TourFormValues } from './NewTourFormState.ts';
import { type CapacityMetric, getRelevantCapacityMetrics } from './tourCapacity.ts';

type TourCapacityFormProps = {
    control: Control<TourFormValues>;
    orders: OrderSummary[];
    compact?: boolean;
    exceededDimensions?: CapacityMetric[];
};

export const TourCapacityForm = ({
    control,
    orders,
    compact = false,
    exceededDimensions = [],
}: TourCapacityFormProps) => {
    const intl = useIntl();
    const relevantMetrics = getRelevantCapacityMetrics(orders);
    const inputLayout = compact
        ? {
              formGroupClassName:
                  'margin-0 min-width-0 display-flex align-items-center justify-content-between gap-5 text-size-12',
              inputContainerClassName: 'width-100 flex-shrink-0',
              bsSize: 'sm' as const,
          }
        : {};

    if (relevantMetrics.size === 0) {
        return null;
    }

    const label = (suffix: string, defaultMessage: string) =>
        intl.formatMessage({
            id: `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}`,
            defaultMessage,
        });

    const inputLabel = (suffix: string, defaultMessage: string) => {
        const fullLabel = label(suffix, defaultMessage);
        const compactId = `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}-compact`;
        return {
            label: compact && intl.messages[compactId] ? intl.formatMessage({ id: compactId }) : fullLabel,
            accessibleLabel: fullLabel,
            title: fullLabel,
        };
    };

    return (
        <section className={compact ? 'flex-shrink-0' : 'margin-top-20'} aria-labelledby='tour-capacity-title'>
            <h4
                id='tour-capacity-title'
                className={
                    compact
                        ? 'margin-0 margin-bottom-5 text-size-14 text-color-gray display-flex align-items-center gap-5'
                        : 'margin-top-0 margin-bottom-10'
                }
            >
                {label('title', 'Vehicle capacity')}
                {exceededDimensions.length > 0 && <CapacityWarning dimensions={exceededDimensions} />}
            </h4>
            <div
                className={
                    compact
                        ? 'display-grid grid-cols-1 grid-cols-2-sm gap-10'
                        : 'display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-20'
                }
            >
                {relevantMetrics.has('weight') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxWeightT'
                        {...inputLabel('max-weight', 'Maximum total weight')}
                        addon='t'
                        min={0}
                    />
                )}
                {relevantMetrics.has('liter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeLiter'
                        {...inputLabel('max-volume-liter', 'Maximum volume in litres')}
                        addon='l'
                        min={0}
                    />
                )}
                {relevantMetrics.has('cubicMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeCubicMeter'
                        {...inputLabel('max-volume-cubic-meter', 'Maximum volume in cubic metres')}
                        addon='m³'
                        min={0}
                    />
                )}
                {relevantMetrics.has('loadingMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxLoadingMeter'
                        {...inputLabel('max-loading-meter', 'Maximum loading meter')}
                        addon='Ldm'
                        min={0}
                    />
                )}
            </div>
        </section>
    );
};
